import * as lodash from "lodash";
import {
    Compiler,
    ComponentFactoryResolver,
    Injectable,
    Injector,
    ReflectiveInjector,
    ResolvedReflectiveProvider,
    SystemJsNgModuleLoader,
    Type,
    NgModuleFactory
} from "@angular/core";
import { Observable } from "rxjs";
import { Http } from "@angular/http";
import * as SystemJSLoader from "systemjs";
import { BASE_URLS, IService, TCITenantAppType } from "@tci/common";
import { NgWindowRefService } from "../ng-shared/ng-services/ng-window-ref.service";
import { TCITenantRemoteServices } from "./tci-tenant-remote-services";
import { LOCAL_TENANT_SVCS, TciTenantModelService } from "./tci-tenant.model";

declare var SystemJS: SystemJSLoader.System;

// const TOKEN_ID = "TCI_INJECTOR";

export interface ITCITenantServiceMap<T> {
    [svc_name: string]: T;
}

export interface ITCITenantServiceLoader {
    getTenantServices<T>(abstract_service_class: Type<T>): Observable<ITCITenantServiceMap<T>>;
}

@Injectable()
export class TCITenantServiceLoader implements ITCITenantServiceLoader {
    private static instance: TCITenantServiceLoader;
    private initCompleted = false;
    private remoteServices: IService[];
    private resolvedProviders: ResolvedReflectiveProvider[];
    private serviceInjector: ReflectiveInjector;

    public static getInstance(
        _injector: Injector,
        http: Http,
        resolver: ComponentFactoryResolver,
        loader: SystemJsNgModuleLoader,
        compiler: Compiler,
        _window: NgWindowRefService,
        remoteSvcs: TCITenantRemoteServices,
        modelSrc: TciTenantModelService
    ): TCITenantServiceLoader {
        if (!TCITenantServiceLoader.instance) {
            TCITenantServiceLoader.instance =
                new TCITenantServiceLoader(_injector, http, resolver, loader, compiler, _window, remoteSvcs, modelSrc);
        }
        return TCITenantServiceLoader.instance;
    }

    public constructor(
        private _injector: Injector,
        private http: Http,
        private resolver: ComponentFactoryResolver,
        private loader: SystemJsNgModuleLoader,
        private compiler: Compiler,
        private _window: NgWindowRefService,
        private remoteSvcs: TCITenantRemoteServices,
        private modelSrc: TciTenantModelService
    ) {
        // _window.nativeWindow[TOKEN_ID] = new OpaqueToken(TOKEN_ID);

        this
            .init()
            .subscribe(res => {
                this.initCompleted = res;
            }, err => {
                console.error(err);
                // TODO: log errors
            }, () => {
            });

    }

    private init(): Observable<boolean> {
        return Observable.create(obs => {
            this.resolvedProviders = ReflectiveInjector.resolve([
                {
                    provide: Injector,
                    useValue: this._injector
                },
                {
                    provide: Http,
                    useValue: this.http
                },
                {
                    provide: TciTenantModelService,
                    useValue: this.modelSrc
                }
            ]);
            this.serviceInjector = ReflectiveInjector.fromResolvedProviders(this.resolvedProviders, this._injector);
            obs.next(true);
            obs.complete();
        });
    }

    // public getTenantServices<T>(abstract_service_class: Type<T>): Observable<T[]> {
    /**
     * Gets Remote Services
     * @param abstract_service_class
     */
    public getTenantServices<T>(abstract_service_class: Type<T> | any): Observable<ITCITenantServiceMap<T>> {
        interface ModuleFacInfo {
            tenant: IService;
            fac: NgModuleFactory<any>;
        }

        return this
            .remoteSvcs
            .getRemoteServices()
            .flatMap(svc => svc)
            // .concat(LOCAL_TENANT_SVCS)
            .filter((tenant: IService) => {
                const cfg = (<any>SystemJS).getConfig();
                const appType = TCITenantAppType.fromString(tenant.name); // get normalized name
                const svcName = appType.getServiceModuleName();
                return cfg.map && cfg.packages && cfg.map[svcName];
            })
            .flatMap((tenant: IService) =>
                /**
                 * The tenant names are from tci-deployer i.e. "wistudio" and "scribe"
                 *  and we are getting the tenant names from backend API /api/v1/uiservices as they match the deployer names
                 *  We are banking on the moduleconfig.json map + package entry
                 *  where the tenant name suffixed with a "-svc" is mapped to the module e.g.
                 *  map:
                 *      "wistudio-svc" :  : "wi-host:",
                 *  package:
                 *      "wistudio-svc" : { "main": "app/app.child.module.js", "defaultExtension": "js" },
                 *
                 * NOTE that this entry is different from the app entry point e..g.
                 * map:
                 *      "wi-studio/client" : "wi-host:",
                 *      "wi-studio/client": "wi-host:",
                 *      "wi-studio/index": "wi-host:",
                 *  package:
                 *      "wi-studio/client": { "main": "app/app.child.module.js", "defaultExtension": "js" },
                 *      "wi-studio/index": { "main": "index.js", "defaultExtension": "js" },
                 *      "wi-studio": { "main": "index.js", "defaultExtension": "js" },
                 *
                 */
                // Observable.create(observer => {
                //   const modFacInfo: ModuleFacInfo = {
                //     tenant,
                //     fac: null
                //   };
                //   Observable
                //     .fromPromise(this.loader.load(TCITenantAppType.fromString(tenant.name).getSvcName()))
                //     .subscribe(
                //       fac => {
                //         modFacInfo.fac = fac;
                //         observer.next(modFacInfo);
                //         observer.complete();
                //       },
                //       error => {
                //         console.warn("Tenant module could not be loaded", tenant, error);
                //         observer.next(modFacInfo);
                //       }
                //     );
                // })
                Observable
                    .fromPromise(this.loader.load(TCITenantAppType.fromString(tenant.name).getServiceModuleName()))
                    .catch(e => {
                        console.error("Tenant was not loaded:", tenant.name, e);
                        return Observable.of(null);
                    })
                    .map(fac => <ModuleFacInfo>{
                        tenant: tenant,
                        fac: fac
                    })
            )
            // .flatMap(modFacInfo => modFacInfo)
            .map((modFacInfo: ModuleFacInfo) => {
                const modRef = modFacInfo.fac ? modFacInfo.fac.create(this.serviceInjector) : null;
                const val = modRef ? modRef.injector.get(abstract_service_class) : null;
                let ret = {};
                if (val) {
                    ret = {
                        [modFacInfo.tenant.name]: val
                    };
                } else {
                    console.warn("No valid response from tenant", modFacInfo);
                }
                console.log("Tenant Services:", ret);
                return ret;
            })
            .reduce((accumulator, value) => {
                return lodash.merge(accumulator, value);
            }, {});
    }

}
